import { authFetch } from '../../api/csrf';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import { Search, XCircle, Video, MapPin, Check, ChevronDown, User } from 'lucide-react';
import Button from '../common/Button/Button';
import PermissionGate from '../common/PermissionGate';
import { API_BASE_URL } from '../../api/config';
import interviewService from '../../api/services/interviewService';
import locationService from '../../api/services/locationService';
import { useAuth } from '../../context/AuthContext';
import fieldSalesService from '../../api/crm/field-sales/fieldSalesService';
import { formatRoleName } from '../../utils/constants';

/**
 * ScheduleInterviewModal - Reusable component for scheduling interviews
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Controls modal visibility
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} props.onSuccess - Callback when interview is successfully scheduled
 * @param {Array} props.candidates - Array of candidate objects
 * @param {Object} props.preselectedCandidate - Optional preselected candidate object
 * @param {Function} props.validateField - Optional custom validation function
 */
const ScheduleInterviewModal = ({
    isOpen,
    onClose,
    onSuccess,
    candidates = [],
    preselectedCandidate = null,
    validateField: externalValidateField = null,
    editData = null,
}) => {
    // Auth context
    const { user } = useAuth();

    // State management
    const [formData, setFormData] = useState({
        candidateId: '',
        interviewerId: '',
        date: '',
        time: '',
        duration: 30,
        type: 'video',
        location: '',
        meetingLink: '',
        notes: '',
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [interviewerSearchTerm, setInterviewerSearchTerm] = useState('');
    const [debouncedInterviewerSearch, setDebouncedInterviewerSearch] = useState('');
    const [showInterviewerDropdown, setShowInterviewerDropdown] = useState(false);
    const [selectedInterviewerData, setSelectedInterviewerData] = useState(null);
    const [candidateSearchTerm, setCandidateSearchTerm] = useState('');
    const [showCandidateDropdown, setShowCandidateDropdown] = useState(false);
    const [defaultInterviewers, setDefaultInterviewers] = useState([]);
    const [isLoadingDefaultInterviewers, setIsLoadingDefaultInterviewers] = useState(false);
    const [showLocationDropdown, setShowLocationDropdown] = useState(false);

    const candidateDropdownRef = useRef(null);
    const interviewerDropdownRef = useRef(null);
    const locationDropdownRef = useRef(null);

    const interviewTypes = [
        { id: 'video', name: 'Video Call', icon: Video },
        { id: 'in_person', name: 'In Person', icon: MapPin },
    ];

    const durationOptions = Array.from({ length: 12 }, (_, i) => (i + 1) * 5);

    // Debounce interviewer search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedInterviewerSearch(interviewerSearchTerm);
        }, 300);

        return () => clearTimeout(timer);
    }, [interviewerSearchTerm]);

    // React Query: Fetch Interviewers using the unified Sales Reps API
    // Roles: hr_manager, hr_executive, employee, super_admin
    const { data: interviewersData, isLoading: isLoadingInterviewers } = useQuery({
        queryKey: ['schedule-interview-employees', debouncedInterviewerSearch],
        queryFn: async () => {
            const params = {
                role: 'super_admin,hr_manager,hr_executive,employee,sales_manager,sales_executive,business_relationship_manager,business_development_executive,dispatch_manager,support_manager',
                limit: debouncedInterviewerSearch && debouncedInterviewerSearch.trim().length >= 2 ? 50 : 20,
                ...(debouncedInterviewerSearch && debouncedInterviewerSearch.trim().length >= 2
                    ? { search: debouncedInterviewerSearch.trim() }
                    : {})
            };
            const response = await fieldSalesService.getSalesReps(params);
            if (response.success && response.data) {
                return response.data;
            }
            return [];
        },
        enabled: isOpen && showInterviewerDropdown,
        staleTime: 0,
    });

    const isSearchingInterviewers = !!debouncedInterviewerSearch.trim();
    const dropdownInterviewers = interviewersData || [];
    const isDropdownLoading = isLoadingInterviewers;

    // Fetch locations using React Query
    const { data: locationsData, isLoading: loadingLocations } = useQuery({
        queryKey: ['locations'],
        queryFn: async () => {
            try {
                const result = await locationService.getLocations();

                if (result.success && result.data) {
                    if (result.data.data?.locations && Array.isArray(result.data.data.locations)) {
                        return result.data.data.locations;
                    }
                    if (result.data.locations && Array.isArray(result.data.locations)) {
                        return result.data.locations;
                    }
                    if (Array.isArray(result.data)) {
                        return result.data;
                    }
                }
                return [];
            } catch (error) {
                return [];
            }
        },
        staleTime: 0,
        retry: 2,
        enabled: isOpen,
    });

    const locations = locationsData || [];

    // Close dropdowns on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            try {
                if (showCandidateDropdown && candidateDropdownRef.current && !candidateDropdownRef.current.contains(event.target)) {
                    setShowCandidateDropdown(false);
                }
                if (showInterviewerDropdown && interviewerDropdownRef.current && !interviewerDropdownRef.current.contains(event.target)) {
                    setShowInterviewerDropdown(false);
                }
                if (showLocationDropdown && locationDropdownRef.current && !locationDropdownRef.current.contains(event.target)) {
                    setShowLocationDropdown(false);
                }
            } catch (_) { }
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside, { passive: true });
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [showCandidateDropdown, showInterviewerDropdown]);

    // Initialize form with preselected candidate
    useEffect(() => {
        if (isOpen && preselectedCandidate) {
            setFormData(prev => ({
                ...prev,
                candidateId: preselectedCandidate.id || preselectedCandidate._id || '',
            }));
            setCandidateSearchTerm(
                preselectedCandidate.name ||
                `${preselectedCandidate.firstName || ''} ${preselectedCandidate.lastName || ''}`.trim() ||
                'Unknown'
            );
        }
    }, [isOpen, preselectedCandidate]);

    // Reset form when modal closes or update when editData changes
    useEffect(() => {
        if (!isOpen) {
            setFormData({
                candidateId: '',
                interviewerId: '',
                date: '',
                time: '',
                duration: 30,
                type: 'video',
                location: '',
                meetingLink: '',
                notes: '',
            });
            setErrors({});
            setCandidateSearchTerm('');
            setInterviewerSearchTerm('');
            setSelectedInterviewerData(null);
            setShowCandidateDropdown(false);
            setShowInterviewerDropdown(false);
        } else if (editData) {
            // Populate form with edit data
            setFormData({
                candidateId: editData.candidateId || '',
                interviewerId: editData.interviewerId || '',
                date: editData.date || '',
                time: editData.time || '',
                duration: editData.duration || 30,
                type: editData.type || 'video',
                location: editData.location || '',
                meetingLink: editData.meetingLink || '',
                notes: editData.notes || '',
            });

            // Set search terms for candidate and interviewer
            setCandidateSearchTerm(editData.candidateName || '');
            setInterviewerSearchTerm(editData.interviewerName || '');

            // Populate basic interviewer data - full details will be fetched by the useEffect below
            setSelectedInterviewerData({
                _id: editData.interviewerId,
                name: editData.interviewerName,
                personalInfo: {
                    firstName: editData.interviewerName?.split(' ')[0] || '',
                    lastName: editData.interviewerName?.split(' ').slice(1).join(' ') || ''
                },
                contactInfo: { email: editData.interviewerEmail }
            });
        }
    }, [isOpen, editData]);

    // Fetch full interviewer details when editing
    useEffect(() => {
        const fetchInterviewerDetails = async () => {
            if (isOpen && editData && editData.interviewerId) {
                try {
                    const baseUrl = API_BASE_URL;
                    const url = `${baseUrl}/hr/employees/${editData.interviewerId}`;
                    const response = await authFetch(url, {
        credentials: 'include',
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            }
                    });

                    if (response.ok) {
                        const data = await response.json();
                        if (data.responseCode === 200 && data.data) {
                            setSelectedInterviewerData(data.data);
                        } else if (data.success && data.data) {
                            setSelectedInterviewerData(data.data);
                        }
                    }
                } catch (error) {
                }
            }
        };

        fetchInterviewerDetails();
    }, [isOpen, editData, API_BASE_URL]);

    // Filter candidates based on search term
    const filteredCandidates = candidates.filter(candidate => {
        if (!candidateSearchTerm) return true;
        const searchLower = candidateSearchTerm.toLowerCase();
        const name = (candidate.name || `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim()).toLowerCase();
        const email = (candidate.email || '').toLowerCase();
        const jobTitle = (candidate.jobTitle || '').toLowerCase();
        return name.includes(searchLower) || email.includes(searchLower) || jobTitle.includes(searchLower);
    });

    // Validation functions
    const getValidationMessage = (name, value) => {
        let msg = '';
        const allowedDurations = durationOptions;
        const isUrl = (u) => {
            try { new URL(u); return true; } catch { return false; }
        };

        switch (name) {
            case 'candidateId':
                if (!value) msg = 'Candidate is required.';
                break;
            case 'interviewerId':
                if (!value) msg = 'Interviewer is required.';
                break;
            case 'date': {
                if (!value) { msg = 'Date is required.'; break; }
                const d = new Date(value);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                if (d < today) msg = 'Interview date cannot be in the past.';
                break;
            }
            case 'time':
                if (!value) msg = 'Time is required.';
                break;
            case 'duration': {
                const dur = parseInt(value, 10);
                if (!allowedDurations.includes(dur)) msg = 'Invalid duration selected.';
                break;
            }
            case 'type':
                if (!value) msg = 'Interview type is required.';
                break;
            case 'meetingLink':
                if (formData.type === 'video') {
                    if (!value) msg = 'Meeting link is required for video interviews.';
                    else if (value.length > 300) msg = 'Meeting link is too long (max 300 chars).';
                    else if (!isUrl(value)) msg = 'Please enter a valid URL.';
                }
                break;
            case 'location':
                if (formData.type === 'in_person') {
                    if (!value || value.trim().length < 2) msg = 'Location is required for in-person interviews.';
                }
                break;
            case 'notes': {
                if (value && value.length > 1000) msg = 'Notes must be ≤ 1000 characters.';
                break;
            }
            default:
                break;
        }
        return msg;
    };

    const validateFieldInternal = (name, value) => {
        const msg = getValidationMessage(name, value);
        setErrors(prev => ({ ...prev, [name]: msg || undefined }));
        return msg;
    };

    const validateField = externalValidateField || validateFieldInternal;

    const validateForm = () => {
        const fieldsToValidate = ['candidateId', 'interviewerId', 'date', 'time', 'duration', 'type', 'location', 'meetingLink', 'notes'];
        const nextErrors = {};

        fieldsToValidate.forEach(f => {
            const msg = getValidationMessage(f, formData[f]);
            if (msg) nextErrors[f] = msg;
        });

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name } = e.target;
        let { value } = e.target;

        // Date validation - prevent past dates
        if (name === 'date' && value) {
            const selectedDate = new Date(value);
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            if (selectedDate < today) {
                setErrors(prev => ({ ...prev, date: 'Interview date cannot be in the past.' }));
                return;
            }
        }

        // Enforce character limit for meetingLink
        if (name === 'meetingLink' && value.length > 300) {
            value = value.slice(0, 300);
        }

        // Enforce character limit for notes
        if (name === 'notes' && value.length > 1000) {
            value = value.slice(0, 1000);
        }

        // Clear location/meetingLink when interview type changes
        const updateData = { [name]: value };
        const errorUpdates = { [name]: undefined };

        if (name === 'type') {
            if (value === 'video') {
                updateData.location = '';
                errorUpdates.location = undefined;
            } else if (value === 'in_person') {
                updateData.meetingLink = '';
                errorUpdates.meetingLink = undefined;
            }
        }

        setFormData(prev => ({
            ...prev,
            ...updateData,
        }));

        setErrors(prev => ({ ...prev, ...errorUpdates }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (loading) {
            return;
        }

        setLoading(true);

        const ok = validateForm();
        if (!ok) {
            setLoading(false);
            return;
        }

        try {
            let candidate = candidates.find((c) => c.id === formData.candidateId || c._id === formData.candidateId);
            const interviewer = selectedInterviewerData;

            // If not found in current candidates list but we are editing, use editData
            if (!candidate && editData && editData.candidateId === formData.candidateId) {
                candidate = {
                    id: editData.candidateId,
                    _id: editData.candidateId,
                    name: editData.candidateName,
                    jobId: editData.jobId
                };
            }

            if (!candidate) {
                throw new Error('Please select a candidate');
            }

            if (!interviewer || !formData.interviewerId) {
                throw new Error('Please select an interviewer');
            }

            // Date validation
            const selectedDate = new Date(formData.date);
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            if (selectedDate < today) {
                throw new Error('Interview date cannot be in the past. Please select a future date.');
            }

            // Determine the interviewer value to send
            let interviewerValue = formData.interviewerId;

            if (interviewer) {
                if (interviewer.userId) {
                    interviewerValue = interviewer.userId;
                } else if (interviewer.user?._id) {
                    interviewerValue = interviewer.user._id;
                } else if (interviewer.user && String(interviewer.user).match(/^[0-9a-fA-F]{24}$/)) {
                    interviewerValue = interviewer.user;
                } else if (interviewer.employeeId) {
                    interviewerValue = interviewer.employeeId;
                } else if (interviewer._id && interviewer._id.match(/^[0-9a-fA-F]{24}$/)) {
                    interviewerValue = interviewer._id;
                } else if (interviewer.id && interviewer.id.match(/^[0-9a-fA-F]{24}$/)) {
                    interviewerValue = interviewer.id;
                }
            }

            // Extract job ID from candidate
            // We allow 'unknown' to pass through because the backend has a fallback 
            // mechanism to find the corresponding job using the candidate's jobTitle.
            const jobId =
                candidate.jobId ||
                candidate.job?._id ||
                candidate.job?.id ||
                (typeof candidate.job === 'string' && candidate.job !== 'unknown' ? candidate.job : null) ||
                (candidate.job && candidate.job !== 'unknown' ? candidate.job : null) ||
                candidate.jobTitle ||
                'unknown';

            const interviewData = {
                candidate: formData.candidateId,  // Backend expects 'candidate' not 'candidateId'
                job: jobId,  // Backend requires job field
                interviewer: interviewerValue,  // Backend expects 'interviewer' not 'interviewerId'
                interviewType: formData.type,
                scheduledDate: formData.date,
                scheduledTime: formData.time,
                duration: parseInt(formData.duration, 10),
                location: formData.type === 'in_person' ? formData.location : '',
                meetingLink: formData.type === 'video' ? formData.meetingLink : '',
                notes: formData.notes || '',
                createdBy: editData?.createdBy || user?.id || user?._id,
            };

            let result;
            if (editData) {
                result = await interviewService.updateInterview(editData.id, interviewData);
            } else {
                result = await interviewService.scheduleInterview(interviewData);
            }

            if (result && (result.success || result.responseCode === 201 || result.responseCode === 200 || result.responseMessage?.toLowerCase().includes('successfully'))) {
                toast.success(editData ? 'Interview updated successfully!' : 'Interview scheduled successfully!');
                if (onSuccess) {
                    onSuccess(result);
                }
                onClose();
            } else {
                // Ensure we capture the error message from various possible response structures
                const errorMessage = result?.responseMessage || result?.message || result?.error || 'Failed to schedule interview';
                toast.error(errorMessage);
                setLoading(false);
                return;
            }
        } catch (err) {
            // Better Error Messaging: Extract the exact reason from the backend response
            const responseError = err.response?.data?.responseMessage || err.response?.data?.message || err.message;
            toast.error(responseError || 'Failed to schedule interview. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-semibold text-gray-900">
                            {editData ? 'Edit Interview' : 'Schedule Interview'}
                        </h2>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <XCircle className="h-6 w-6" />
                        </button>
                    </div>

                    <form noValidate onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Candidate Field */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Candidate <span className="text-red-500">*</span>
                                </label>
                                <div className="relative candidate-dropdown" ref={candidateDropdownRef}>
                                    <input
                                        type="text"
                                        placeholder={editData ? "" : `Search candidate... (${candidates.length} available)`}
                                        value={candidateSearchTerm}
                                        maxLength={150}
                                        disabled={!!editData}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            const filteredValue = value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 150);
                                            setCandidateSearchTerm(filteredValue);
                                            setShowCandidateDropdown(true);

                                            if (formData.candidateId) {
                                                setFormData(prev => ({ ...prev, candidateId: '' }));
                                            }
                                            validateField('candidateId', '');
                                        }}
                                        onBlur={() => validateField('candidateId', formData.candidateId)}
                                        onFocus={() => !editData && setShowCandidateDropdown(true)}
                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${editData ? 'bg-gray-100 cursor-not-allowed border-gray-200 text-gray-700' : 'border-gray-300'
                                            }`}
                                    />
                                    {showCandidateDropdown && (
                                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                            {filteredCandidates.length > 0 ? (
                                                filteredCandidates.map((candidate) => (
                                                    <div
                                                        key={candidate.id || candidate._id}
                                                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                                                        onClick={() => {
                                                            setFormData(prev => ({ ...prev, candidateId: candidate.id || candidate._id }));
                                                            setCandidateSearchTerm(
                                                                candidate.name ||
                                                                `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim() ||
                                                                'Unknown'
                                                            );
                                                            setShowCandidateDropdown(false);
                                                            validateField('candidateId', candidate.id || candidate._id);
                                                        }}
                                                    >
                                                        <div className="font-medium text-gray-900">
                                                            {candidate.name || `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim() || 'Unknown'}
                                                        </div>
                                                        <div className="text-sm text-gray-500">
                                                            {candidate.jobTitle || 'N/A'} • {(candidate.email || candidate.status || '').toString()}
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="px-3 py-2 text-gray-500 text-sm">No candidates found</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {errors.candidateId && (
                                    <p className="mt-1 text-sm text-red-600">{errors.candidateId}</p>
                                )}
                                {formData.candidateId && (
                                    <div className="mt-2 p-3 bg-green-50 rounded-lg border border-green-100 min-w-0">
                                        <div className="text-sm min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                    <div className="bg-green-100 p-1 rounded-full">
                                                        <Check className="w-3 h-3 text-green-600" />
                                                    </div>
                                                <span className="font-semibold text-green-800">Selected Candidate</span>
                                            </div>
                                            <div className="font-medium text-gray-900 whitespace-normal break-words">
                                                {(() => {
                                                    const candidate = candidates.find(c => c.id === formData.candidateId || c._id === formData.candidateId);
                                                    if (candidate) {
                                                        return candidate.name || `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim();
                                                    }
                                                    return candidateSearchTerm || 'Selected Candidate';
                                                })()}
                                            </div>
                                            {(() => {
                                                const candidate = candidates.find(c => c.id === formData.candidateId || c._id === formData.candidateId);
                                                const email = candidate?.email || editData?.candidateEmail;
                                                const jobTitle = candidate?.jobTitle || editData?.jobTitle;

                                                if (email || jobTitle) {
                                                    return (
                                                        <div className="text-gray-600 mt-0.5 text-xs whitespace-normal break-words">
                                                            {email}
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })()}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Interviewer Field */}
                            <div className="min-w-0">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Interviewer <span className="text-red-500">*</span>
                                </label>
                                <div className="relative interviewer-dropdown min-w-0" ref={interviewerDropdownRef}>
                                    <div
                                        onClick={() => {
                                            if (showInterviewerDropdown) {
                                                validateField('interviewerId', formData.interviewerId);
                                            } else {
                                                setInterviewerSearchTerm("");
                                            }
                                            setShowInterviewerDropdown(!showInterviewerDropdown);
                                        }}
                                        className={`w-full px-3 py-2 border rounded-lg cursor-pointer flex items-start justify-between gap-3 hover:border-gray-400 focus-within:border-agrosaf-green focus-within:ring-1 focus-within:ring-agrosaf-green min-w-0 ${errors.interviewerId ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'
                                            }`}
                                    >
                                        <span className={`${formData.interviewerId && selectedInterviewerData ? "text-gray-900" : "text-gray-500"} flex-1 min-w-0 break-words whitespace-normal text-left overflow-hidden`}>
                                            {formData.interviewerId && selectedInterviewerData
                                                ? `${selectedInterviewerData.personalInfo?.firstName || ""} ${selectedInterviewerData.personalInfo?.lastName || ""} - ${selectedInterviewerData.employmentInfo?.position || formatRoleName(selectedInterviewerData.user?.role || selectedInterviewerData.role)} (${selectedInterviewerData.employmentInfo?.department || selectedInterviewerData.department || ""})`
                                                : formData.interviewerId
                                                    ? `Selected Interviewer: ${formData.interviewerId}`
                                                    : "Search interviewer..."}
                                        </span>
                                        <ChevronDown
                                            className={`w-4 h-4 text-gray-400 transition-transform ${showInterviewerDropdown ? "transform rotate-180" : ""
                                                }`}
                                        />
                                    </div>

                                    {showInterviewerDropdown && (
                                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden min-w-0">
                                            <div className="p-2 border-b border-gray-200">
                                                <div className="relative">
                                                    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                                    <input
                                                        type="text"
                                                        placeholder="Search by employee name or ID..."
                                                        value={interviewerSearchTerm}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            const filteredValue = value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 150);
                                                            setInterviewerSearchTerm(filteredValue);

                                                            if (formData.interviewerId) {
                                                                setFormData(prev => ({ ...prev, interviewerId: '' }));
                                                                setSelectedInterviewerData(null);
                                                            }
                                                            validateField('interviewerId', '');
                                                        }}
                                                        onClick={(e) => e.stopPropagation()}
                                                        maxLength={150}
                                                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-agrosaf-green focus:border-transparent text-sm"
                                                        autoFocus
                                                    />
                                                </div>
                                            </div>

                                            <div className="max-h-64 overflow-y-auto min-w-0">
                                                {isDropdownLoading ? (
                                                    <div className="p-4 text-center text-gray-500 text-sm">
                                                        {isSearchingInterviewers ? 'Searching...' : 'Loading interviewers...'}
                                                    </div>
                                                ) : dropdownInterviewers.length > 0 ? (
                                                    dropdownInterviewers.map((employee) => {
                                                        const employeeName = employee.name || 'Unknown';
                                                        const employeeDepartment = employee.department || 'No Dept';
                                                        const employeeRole = (employee.role || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                                                        const employeeEmail = employee.officialEmail || employee.email || employee.personalEmail || 'No email';

                                                        return (
                                                            <div
                                                                key={employee._id || employee.id}
                                                                onClick={() => {
                                                                    const userId =
                                                                        employee.userId ||
                                                                        employee.user?._id ||
                                                                        employee.user ||
                                                                        employee._id ||
                                                                        employee.id ||
                                                                        employee.employeeId;
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        interviewerId: userId,
                                                                    }));
                                                                    setSelectedInterviewerData({
                                                                        ...employee,
                                                                        userId: employee.userId || employee.user?._id || employee.user,
                                                                        personalInfo: {
                                                                            firstName: employeeName.split(' ')[0] || '',
                                                                            lastName: employeeName.split(' ').slice(1).join(' ') || ''
                                                                        },
                                                                        employmentInfo: {
                                                                            department: employeeDepartment,
                                                                            position: employeeRole
                                                                        },
                                                                        contactInfo: {
                                                                            email: employeeEmail
                                                                        }
                                                                    });
                                                                    setShowInterviewerDropdown(false);
                                                                    setInterviewerSearchTerm(employeeName);
                                                                    validateField('interviewerId', userId);
                                                                }}
                                                                className={`px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 whitespace-normal break-words min-w-0 ${formData.interviewerId === (employee.userId || employee.user?._id || employee.user || employee._id || employee.id || employee.employeeId)
                                                                    ? "bg-blue-50"
                                                                    : ""
                                                                    }`}
                                                            >
                                                                <div className="text-sm font-medium text-gray-900 whitespace-normal break-words">
                                                                    {employeeName} ({employee.employeeId || 'No ID'})
                                                                </div>
                                                                <div className="text-xs text-gray-500 whitespace-normal break-words">
                                                                    <span className="text-blue-600 font-medium">{employeeRole}</span> - {employeeDepartment} | {employeeEmail}
                                                                </div>
                                                            </div>
                                                        );
                                                    })
                                                ) : (
                                                    <div className="p-4 text-center text-gray-500 text-sm">
                                                        {isSearchingInterviewers
                                                            ? "No interviewers found"
                                                            : "No interviewers available. Try searching to load more."}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                    {showInterviewerDropdown
                                        ? isSearchingInterviewers
                                            ? `Found ${dropdownInterviewers.length} employee(s)`
                                            : `Showing ${dropdownInterviewers.length} recent employees`
                                        : ""}
                                </div>
                                {selectedInterviewerData && (
                                    <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-100 min-w-0">
                                        <div className="text-sm min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <div className="bg-blue-100 p-1 rounded-full">
                                                    <User className="w-3 h-3 text-blue-600" />
                                                </div>
                                                <span className="font-semibold text-blue-800">Selected Interviewer</span>
                                            </div>
                                            <div className="font-medium text-gray-900 whitespace-normal break-words">
                                                {selectedInterviewerData.personalInfo?.firstName || selectedInterviewerData.name?.split(' ')[0] || ""}{" "}
                                                {selectedInterviewerData.personalInfo?.lastName || selectedInterviewerData.name?.split(' ').slice(1).join(' ') || ""}
                                            </div>
                                            <div className="text-gray-600 mt-0.5 text-xs whitespace-normal break-words">
                                                {selectedInterviewerData.contactInfo?.personalEmail || selectedInterviewerData.contactInfo?.email || "N/A"}
                                            </div>
                                            {/* <div className="text-gray-500 text-xs whitespace-normal break-words mt-0.5 italic">
                                                {selectedInterviewerData.employmentInfo?.department || selectedInterviewerData.department || "N/A"} •{" "}
                                                {selectedInterviewerData.employmentInfo?.position || selectedInterviewerData.position || "N/A"}
                                            </div> */}
                                        </div>
                                    </div>
                                )}
                                {errors.interviewerId && (
                                    <p className="mt-1 text-sm text-red-600">{errors.interviewerId}</p>
                                )}
                            </div>

                            {/* Date Field */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Date <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="date"
                                    name="date"
                                    value={formData.date}
                                    onChange={handleInputChange}
                                    onClick={(e) => e.target.showPicker && e.target.showPicker()}
                                    onBlur={(e) => validateField('date', e.target.value)}
                                    min={new Date().toISOString().split('T')[0]}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                                />
                                {errors.date && (
                                    <p className="mt-1 text-sm text-red-600">{errors.date}</p>
                                )}
                            </div>

                            {/* Time Field */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Time <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="time"
                                    name="time"
                                    value={formData.time}
                                    onChange={handleInputChange}
                                    onClick={(e) => e.target.showPicker && e.target.showPicker()}
                                    onBlur={(e) => validateField('time', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                                />
                                {errors.time && (
                                    <p className="mt-1 text-sm text-red-600">{errors.time}</p>
                                )}
                            </div>

                            {/* Duration Field */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Duration (minutes)
                                </label>
                                <select
                                    name="duration"
                                    value={formData.duration}
                                    onChange={handleInputChange}
                                    onBlur={(e) => validateField('duration', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                                >
                                    {durationOptions.map((minutes) => (
                                        <option key={minutes} value={minutes}>
                                            {minutes} minutes
                                        </option>
                                    ))}
                                </select>
                                {errors.duration && (
                                    <p className="mt-1 text-sm text-red-600">{errors.duration}</p>
                                )}
                            </div>

                            {/* Interview Type Field */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Interview Type <span className="text-red-500">*</span>
                                </label>
                                <select
                                    name="type"
                                    value={formData.type}
                                    onChange={handleInputChange}
                                    onBlur={(e) => validateField('type', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                                >
                                    {interviewTypes.map(type => (
                                        <option key={type.id} value={type.id}>{type.name}</option>
                                    ))}
                                </select>
                                {errors.type && (
                                    <p className="mt-1 text-sm text-red-600">{errors.type}</p>
                                )}
                            </div>
                        </div>

                        {/* Location Field (conditional) */}
                        {formData.type === 'in_person' && (
                            <div className="relative" ref={locationDropdownRef}>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Location <span className="text-red-500">*</span>
                                </label>
                                <div
                                    onClick={() => !loadingLocations && setShowLocationDropdown(!showLocationDropdown)}
                                    className={`w-full px-3 py-2 border rounded-lg flex items-center justify-between cursor-pointer
                                        ${errors.location ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'}
                                        ${loadingLocations ? 'opacity-70 cursor-not-allowed' : 'hover:border-gray-400'}
                                    `}
                                >
                                    <span className={`block truncate mr-2 ${!formData.location ? 'text-gray-500' : 'text-gray-900'}`}>
                                        {formData.location || (loadingLocations ? 'Loading locations...' : 'Select Location')}
                                    </span>
                                    <ChevronDown
                                        className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${showLocationDropdown ? "transform rotate-180" : ""}`}
                                    />
                                </div>

                                {showLocationDropdown && (
                                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                        {locations.length > 0 ? (
                                            locations.map((location) => {
                                                const val = location.fullAddress || location.name;
                                                const hasAddress = !!location.fullAddress;

                                                return (
                                                    <div
                                                        key={location.id || location._id}
                                                        onClick={() => {
                                                            setFormData(prev => ({ ...prev, location: val }));
                                                            validateField('location', val);
                                                            setShowLocationDropdown(false);
                                                        }}
                                                        className={`px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0
                                                            ${formData.location === val ? 'bg-blue-50' : ''}
                                                        `}
                                                    >
                                                        <div className="font-medium text-gray-900 truncate">
                                                            {location.name}
                                                        </div>
                                                        {hasAddress && (
                                                            <div className="text-sm text-gray-500 overflow-x-auto whitespace-nowrap pb-1 scrollbar-thin">
                                                                {location.fullAddress}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div className="px-3 py-2 text-gray-500 text-sm">No locations available</div>
                                        )}
                                    </div>
                                )}
                                {errors.location && (
                                    <p className="mt-1 text-sm text-red-600">{errors.location}</p>
                                )}
                            </div>
                        )}

                        {/* Meeting Link Field (conditional) */}
                        {formData.type === 'video' && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Meeting Link <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="url"
                                    name="meetingLink"
                                    value={formData.meetingLink}
                                    maxLength={300}
                                    onChange={handleInputChange}
                                    onBlur={(e) => validateField('meetingLink', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                                    placeholder="https://zoom.us/j/..."
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Required for video interviews
                                </p>
                                {errors.meetingLink && (
                                    <p className="mt-1 text-sm text-red-600">{errors.meetingLink}</p>
                                )}
                            </div>
                        )}

                        {/* Notes Field */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Notes <span className="text-xs text-gray-500">(optional)</span>
                            </label>
                            <textarea
                                name="notes"
                                value={formData.notes}
                                maxLength={1000}
                                onChange={handleInputChange}
                                onBlur={(e) => validateField('notes', e.target.value)}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                                placeholder="Additional notes or agenda for the interview..."
                            />
                            <div className="mt-1 flex justify-between text-xs text-gray-500">
                                <span>{Math.min(formData.notes?.length || 0, 1000)}/1000</span>
                            </div>
                            {errors.notes && (
                                <p className="mt-1 text-sm text-red-600">{errors.notes}</p>
                            )}
                        </div>



                        {/* Action Buttons */}
                        <div className="flex justify-end space-x-3 pt-4">
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={onClose}
                            >
                                Cancel
                            </Button>
                            <PermissionGate permission="recruitment" action={editData ? "update" : "create"}>
                                <Button
                                    type="submit"
                                    variant="primary"
                                    className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                                    disabled={loading}
                                >
                                    {loading ? (editData ? 'Updating...' : 'Scheduling...') : (editData ? 'Update Interview' : 'Schedule Interview')}
                                </Button>
                            </PermissionGate>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ScheduleInterviewModal;
